import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { format12Hour, timeToSeconds } from '../helpers';
import { Range } from '../models';

export class TimeValidators {
  static startBeforeEnd(startControl: string, endControl: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const start = group.get(startControl)?.value;
      const end = group.get(endControl)?.value;

      if (!start || !end) return null;

      const toSeconds = (v: string) => {
        const [h, m] = v.split(':').map(Number);
        if (h === 24) return 24 * 3600;
        return h * 3600 + m * 60;
      };

      return toSeconds(start) < toSeconds(end) ? null : { timeRange: true };
    };
  }

  static isHHMM(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const regex = /^((([0-1]?\d|2[0-3]):[0-5]\d)|(24:00))$/;
    return regex.test(value) ? null : { hhmm: true };
  }

  static noOverlapValidator(
    startControl: string,
    endControl: string,
    existing: Range<string>[]
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const start = group.get(startControl)?.value;
      const end = group.get(endControl)?.value;
      if (!start || !end) return null;

      const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h === 24 ? 24 * 60 : h * 60 + m;
      };

      const s = toMinutes(start);
      const e = toMinutes(end);

      let overlaping = '';
      const overlap = existing.some((r) => {
        const rs = toMinutes(r.start);
        const re = toMinutes(r.end);
        const result = s < re && e > rs;
        if (result) {
          overlaping = `${format12Hour(r.start)} - ${format12Hour(r.end)} `;
        }
        return result;
      });
      return overlap
        ? {
            overlap: {
              overlaping
            }
          }
        : null;
    };
  }

  static arrayNoOverlap(
    startControlName: string,
    endControlName: string
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const array = control as FormArray;
      if (!array || !array.controls.length) return null;

      const values = array.controls.map((c, index) => {
        const group = c as FormGroup;
        return {
          start: group.get(startControlName)?.value || '',
          end: group.get(endControlName)?.value || '',
          index: index + 1
        };
      });

      const parseTime = (t: string) => {
        if (!t || !t.includes(':')) return -1;
        const [h, m] = t.split(':').map((v) => parseInt(v, 10));
        if (isNaN(h) || isNaN(m)) return -1;
        return h * 60 + m;
      };

      for (let i = 0; i < values.length; i++) {
        const s1 = parseTime(values[i].start);
        const e1 = parseTime(values[i].end);
        if (s1 === -1 || e1 === -1) continue;

        for (let j = i + 1; j < values.length; j++) {
          const s2 = parseTime(values[j].start);
          const e2 = parseTime(values[j].end);
          if (s2 === -1 || e2 === -1) continue;

          // Check if [s1, e1] overlaps with [s2, e2]
          // Overlap exists if NOT (one starts after the other ends)
          if (!(e1 <= s2 || e2 <= s1)) {
            return {
              overlap: {
                first: values[i].index,
                second: values[j].index
              }
            };
          }
        }
      }
      return null;
    };
  }

  private static createTimeComparisonValidator(
    getBoundTime: (
      control: AbstractControl
    ) => string | Date | number | null | undefined,
    comparator: (time: number, bound: number) => boolean,
    errorKey: 'timeHigherThan' | 'timeLowerThan'
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const time = control.value;
      const boundTime = getBoundTime(control);

      if (!time || !boundTime) {
        return null;
      }

      try {
        return comparator(timeToSeconds(time), timeToSeconds(boundTime))
          ? null
          : { [errorKey]: true };
      } catch {
        return null;
      }
    };
  }

  static isTimeLowerThanControl(controlName: string): ValidatorFn {
    return this.createTimeComparisonValidator(
      (control) => control.root?.get(controlName)?.value,
      (time, bound) => time < bound,
      'timeLowerThan'
    );
  }

  static isTimeHigherThanControl(controlName: string): ValidatorFn {
    return this.createTimeComparisonValidator(
      (control) => control.root?.get(controlName)?.value,
      (time, bound) => time > bound,
      'timeHigherThan'
    );
  }
}
